// Angular
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';

// Ionic
import {
  IonInput,
  IonIcon,
  IonButton,
  IonSelect,
  IonSelectOption,
  AlertController
} from '@ionic/angular/standalone';

// Local library
import { UnsubscribeObservableService } from '@amwager/util';
import { AccountFacade } from '@amwager/account/data-access';
import { Subscription, takeUntil } from 'rxjs';
import { errorCodeHandlerResponse } from '@amwager/util-ts';
import { CommonFacade, PusherService, SessionService } from '@amwager/data-access';
import { WagerFacade } from '@amwager/wager/data-access';
import { AuthFacade } from '@amwager/authentication/data-access';
import { FundingFacade } from '@amwager/funding/data-access';
import { TicketFacade } from '@amwager/ticket/data-access';

@Component({
  selector: 'feature-ion-responsible-gaming',
  templateUrl: './responsible-gaming.component.html',
  styleUrls: ['./responsible-gaming.component.scss'],
  standalone: true,
  imports: [
    IonInput,
    IonIcon,
    IonButton,
    IonSelect,
    IonSelectOption,
    CommonModule,
    ReactiveFormsModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResponsibleGamingComponent extends UnsubscribeObservableService implements OnInit {

  private subsSelfExcludeOrSuspendResponse!: Subscription;
  private subsSelfExcludeOrSuspendVerificationResponse!: Subscription;
  responsibleGamingForm!: FormGroup;
  responsibleType!: string;
  verificationToken!: string;
  isProccesing: boolean = false;

  @Output() doCloseProfileFn = new EventEmitter<boolean>();

  constructor(
    private readonly pusherService: PusherService,
    private readonly authFacade: AuthFacade,
    private readonly ticketFacade: TicketFacade,
    private readonly fundingFacade: FundingFacade,
    private readonly accountFacade: AccountFacade,
    private readonly commonFacade: CommonFacade,
    private readonly wagerFacade: WagerFacade,
    private readonly formBuilder: FormBuilder,
    private readonly alertCtrl: AlertController,
    private readonly sessionService: SessionService,
    private readonly router: Router,
    private readonly ngZone: NgZone,
    readonly cdRef: ChangeDetectorRef
  ) {

    super();
  }

  // convenience getter for easy access to form fields
  get formVal() { return this.responsibleGamingForm?.controls }

  ngOnInit() {
    this.initForm();
    this.responsibleType = 'timeout';

    this.formVal['days'].setValidators(
      [Validators.required, Validators.min(3), Validators.max(365)]
    );
    this.formVal['days'].updateValueAndValidity();
  }

  private initForm(): void {
    this.responsibleGamingForm = this.formBuilder.group({
      days: [''],
      selfExclude: ['']
    });
  }

  responsibleTypeIs(type: string): void {
    this.responsibleType = type;
    this.responsibleGamingForm.reset();

    this.formVal['days'].clearValidators();
    this.formVal['selfExclude'].clearValidators();

    //re init.
    this.initForm();

    switch (type) {

      case 'timeout':

          this.formVal['days'].setValidators(
            [Validators.required, Validators.min(3), Validators.max(365)]
          );
          this.formVal['days'].updateValueAndValidity();
        break;
      case 'self-exclude':

          this.formVal['selfExclude'].setValidators(
            [Validators.required]
          );
          this.formVal['selfExclude'].updateValueAndValidity();
        break;
    }

  }

  doresponsibleGamingForm(): void {
    if (!navigator.onLine) {
      this.commonFacade.setIsConnectedInternet('ResponsibleGamingComponent:doresponsibleGamingForm', false);
      return;
    }

    if ( (this.responsibleGamingForm.invalid || this.responsibleGamingForm.errors) &&
      !this.isProccesing ) {

        if (this.responsibleType === 'timeout')
          this.formVal['days'].markAsTouched();
        else
          this.formVal['selfExclude'].markAsTouched();

      return;
    }

    this.isProccesing = true;

    const isExclusion: boolean = this.responsibleType === 'self-exclude' ? true : false;
    const { days, selfExclude } = this.responsibleGamingForm.value;

    this.responsibleGamingForm.disable();

    this.accountFacade.selfExcludeOrSuspend(
      Number(days) || 0, Number(selfExclude) || 0, isExclusion
    );
    this.subsSelfExcludeOrSuspendResponse = this.accountFacade.isSelfExcludeOrSuspendSuccess$()
      .pipe(takeUntil(this.toUnsubscribe$))
      .subscribe(({response, isSuccess}) => {

        this.responsibleGamingForm.enable();
        this.isProccesing = false;

        // no action if isSuccess is undefined, the actions are in the effects
        if (isSuccess === undefined) return;

        if (isSuccess) {
          this.verificationToken = response.VerificationToken;
          this.showAlertMessage(this.messageToShow());

          this.subsSelfExcludeOrSuspendResponse?.unsubscribe();
          this.cdRef.markForCheck(); // re-render for template

          return;
        }

        this.responsibleGamingForm.setErrors({
          apiResponse:response?.error?.MessageCode
            ? response?.error?.Message || errorCodeHandlerResponse(response?.status)  // general error handling
            : errorCodeHandlerResponse(response?.status)  // general error handling
        });

        this.subsSelfExcludeOrSuspendResponse?.unsubscribe();

        this.cdRef.markForCheck(); // re-render for template
      });
  }

  doVerification(verificationToken: string): void {

    this.accountFacade.selfExcludeOrSuspendVerification( verificationToken );
    this.subsSelfExcludeOrSuspendVerificationResponse =
      this.accountFacade.isSelfExcludeOrSuspendVerificationSuccess$()
        .pipe(takeUntil(this.toUnsubscribe$))
        .subscribe(({response, isSuccess}) => {

          // no action if isSuccess is undefined or success, the actions are in the effects
          if (isSuccess === undefined) return;

          if (isSuccess) {

            this.responsibleGamingForm.reset();
            this.doCloseProfileFn.emit();

            // pusher
            this.pusherService.unSubscribeCustomer();

            // cleanup storage
            this.sessionService.resetLogout();

            this.commonFacade.setIsAuthenticated(
              false, "ResponsibleGamingComponent:doVerification"
            );
            this.wagerFacade.setIsShowBetPreview(false);

            // don't delete and comment
            this.authFacade.loginGuest();

            // redirect to login screen
            this.router.navigate(['/login']);

            // clear the state manangement
            this.accountFacade.clearState('ResponsibleGamingComponent:doVerification');
            this.authFacade.clearState('ResponsibleGamingComponent:doVerification');
            this.fundingFacade.clearState('ResponsibleGamingComponent:doVerification');
            this.ticketFacade.clearState('ResponsibleGamingComponent:doVerification');
            this.wagerFacade.clearState('ResponsibleGamingComponent:doVerification');
            this.commonFacade.clearState('ResponsibleGamingComponent:doVerification');

            // old logic
            // this.sessionService.resetLogout();
            // this.commonFacade.setIsAuthenticated(false, "ResponsibleGamingComponent:doVerification");
            // this.wagerFacade.setIsShowBetPreview(false);

            // this.router.navigate(['/login']);

            this.subsSelfExcludeOrSuspendVerificationResponse?.unsubscribe();
            this.cdRef.markForCheck(); // re-render for template
            return;
          }

          this.responsibleGamingForm.setErrors({
            apiResponse:response?.error?.MessageCode
              ? response?.error?.Message || errorCodeHandlerResponse(response?.status)  // general error handling
              : errorCodeHandlerResponse(response?.status)  // general error handling
          });

          this.subsSelfExcludeOrSuspendVerificationResponse?.unsubscribe();

          this.cdRef.markForCheck(); // re-render for template
        });
  }

  messageToShow(): string {

    let displayOption = '';
    let displayPeriod = '';
    let message = '';

    switch (this.responsibleType) {

      case 'timeout':
          displayOption = 'Timeout';
        break;

      case 'self-exclude':
          displayOption = 'Self-Exclude';
        break;
    }

    if (this.responsibleType === 'timeout') {
      displayPeriod = this.formVal['days'].value + ' days';
    }
    else {
      if (Number(this.formVal['selfExclude'].value) === 1) {
        displayPeriod = this.formVal['selfExclude'].value + ' year';
      }
      else if (Number(this.formVal['selfExclude'].value) === 999) {
        displayPeriod = 'a Lifetime';
      }
      else {
        displayPeriod = this.formVal['selfExclude'].value + ' years';
      }
    }

    message = `Are you sure you wish to ${ displayOption }
    from gambling for ${ displayPeriod } beginning now ?`;

    return message;
  }

  async showAlertMessage(message: string): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Info',
      message: message,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
          }
        },
        {
          text: 'Proceed',
          handler: () => {
            this.ngZone.run(()=>{
              this.doVerification(this.verificationToken);
            });
          }
        }
      ]
    });

    await alert.present();
  }

}
